import { createI18n } from 'vue-i18n'
import getCookie from '~/core/utils/getCookie'
import getBrowserLocale from '~/core/utils/getBrowserLocale'

// https://vue-i18n.intlify.dev/guide/essentials/datetime.html#basic-usage
const datetimeFormats = {
  'en-us': {
    // 5/20/2022
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    // May 20, 2022
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    // Fri, May 20, 2022, 10:30 AM
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
    // May 20
    day: {
      month: 'short',
      day: 'numeric',
    },
    // 10:30 AM
    time: {
      hour: 'numeric',
      minute: 'numeric',
    },
    // May 20, 2022, 10:30 AM
    datetime_short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // 5/20/2022, 10:30 AM
    datetime_numeric: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // May 2022
    month_year: {
      year: 'numeric',
      month: 'short',
    },
    // May
    month: {
      month: 'short',
    },
    // 2022
    year: {
      year: 'numeric',
    },
    // May 20
    medium_without_year: {
      month: 'short',
      day: 'numeric',
    },
    // Fri, May 20, 2022
    medium_with_weekday: {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    },
    // Fri, May 20, 10:30 AM
    long_without_year: {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  'en-gb': {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
    day: {
      month: 'short',
      day: 'numeric',
    },
    time: {
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_numeric: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // January
    month: {
      month: 'long',
    },
    medium_without_year: {
      month: 'short',
      day: 'numeric',
    },
    medium_with_weekday: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
    long_without_year: {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  'de': {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
    day: {
      month: 'short',
      day: 'numeric',
    },
    time: {
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_numeric: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // January
    month: {
      month: 'long',
    },
    medium_without_year: {
      month: 'short',
      day: 'numeric',
    },
    medium_with_weekday: {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long_without_year: {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  'fr': {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
    day: {
      month: 'short',
      day: 'numeric',
    },
    time: {
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_numeric: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // January
    month: {
      month: 'long',
    },
    medium_without_year: {
      month: 'short',
      day: 'numeric',
    },
    medium_with_weekday: {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long_without_year: {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  'es': {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    medium: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
    day: {
      month: 'short',
      day: 'numeric',
    },
    time: {
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    datetime_numeric: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    // January
    month: {
      month: 'long',
    },
    medium_without_year: {
      month: 'short',
      day: 'numeric',
    },
    medium_with_weekday: {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long_without_year: {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
}

const numberFormats = {
  'en-us': {
    'currency': {
      style: 'currency',
      currency: 'USD',
      notation: 'standard',
    },
    'nearest-tenth': {
      style: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    },
    'nearest-hundreds': {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    'percent': {
      style: 'percent',
      useGrouping: false,
    },
  },
}

// TODO: detect language
// set fallback locale

// function getLocaleFromProviderCountry() {
//   if (!currentOrOnlyProvider)
//     return
//   const providerCountry = currentOrOnlyProvider.country.toLowerCase()
//   let localeCode
//   switch (providerCountry) {
//     case 'gb': localeCode = 'en-gb'; break
//     case 'es': localeCode = 'es'; break
//     case 'fr': localeCode = 'fr'; break
//     case 'de': localeCode = 'de'; break
//     case 'us': default: localeCode = 'en-us'
//   }
//   return localeCode
// }

function getInitialLocale() {
  return getCookie('django_language')
    // || getLocaleFromProviderCountry()
    || getBrowserLocale()
}

export function install(app) {
  const i18n = createI18n({
    legacy: false,
    locale: getInitialLocale(),
    fallbackLocale: 'en-us',
    messages: {},
    datetimeFormats,
    numberFormats,
    fallbackWarn: false,
    missingWarn: false,
  })
  app.use(i18n)
}
